/**
 * Picking the words and the video a learner sees when a course exists in several languages. Pure, so
 * the API (quiz questions) and the web player (titles, descriptions, the switcher) cannot disagree.
 * The row's own text is always the fallback: a half-translated course never shows an empty title.
 */

import { DEFAULT_TRAINING_LANGUAGE, TRAINING_LANGUAGES, type TrainingLanguage } from '../enums.js';

type TextTranslations = Partial<Record<TrainingLanguage, { title?: string; description?: string }>>;

export interface ResolvableText {
  readonly title: string;
  readonly description: string | null;
  /** The language `title` / `description` are written in. Absent → English. */
  readonly language?: TrainingLanguage | undefined;
}

/** `title` and `description` in `wanted`, each falling back to the row's own text when that language
 * has none (or only whitespace) for it. */
export function resolveTrainingText(
  base: ResolvableText,
  translations: TextTranslations | undefined,
  wanted: TrainingLanguage | null | undefined,
): { title: string; description: string | null } {
  const own = base.language ?? DEFAULT_TRAINING_LANGUAGE;
  if (!wanted || wanted === own) return { title: base.title, description: base.description };
  const t = translations?.[wanted];
  return {
    title: t?.title?.trim() ? t.title : base.title,
    description: t?.description?.trim() ? t.description : base.description,
  };
}

/** The languages a lesson or course can be shown in, in the fixed `TRAINING_LANGUAGES` order: its
 * own, every language it has a translation for and every language it has a video in. */
export function availableTrainingLanguages(input: {
  readonly language?: TrainingLanguage | undefined;
  readonly translations?: TextTranslations | undefined;
  readonly variants?: ReadonlyArray<{ readonly language: TrainingLanguage }> | undefined;
}): TrainingLanguage[] {
  const present = new Set<TrainingLanguage>([input.language ?? DEFAULT_TRAINING_LANGUAGE]);
  for (const key of Object.keys(input.translations ?? {})) {
    if ((TRAINING_LANGUAGES as readonly string[]).includes(key)) present.add(key as TrainingLanguage);
  }
  for (const variant of input.variants ?? []) present.add(variant.language);
  return TRAINING_LANGUAGES.filter((l) => present.has(l));
}
